import { useImperativeHandle, useState, forwardRef, useMemo } from "react";
import { useLocalStorage } from "../../utils/localstorage";
import { Text } from "../../components";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Toolbar,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { immutableReplaceAt } from "../../utils/helpers";
import { formatRelative } from "date-fns";
import DeleteIcon from "@material-ui/icons/DeleteForever";
import { colors } from "../../theme/variables";
import CloseIcon from "@material-ui/icons/Close";
import _ from "lodash";
import { useEnv } from "../../plugins/env";
import ConfirmChoiceDialog from "../../components/ConfirmChoiceDialog";
import * as Icons from "@material-ui/icons";

const useStyles = makeStyles({
  sidebar: {
    border: "none",
    zIndex: 1,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "300px",
    minWidth: "300px",
  },
  toolbar: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    padding: "12px 16px",
    minHeight: "64px",
  },
  title: {
    fontWeight: 600,
    fontSize: "18px",
    color: "#1a1a1a",
    flex: 1,
  },
  list: {
    overflowY: "auto",
    cursor: "pointer",
    flex: 1,
    padding: "8px",
  },
  listItem: {
    borderRadius: "6px",
    marginBottom: "4px",
    padding: "8px 12px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "&.Mui-selected": {
      backgroundColor: "#e3f2fd",
      "&:hover": {
        backgroundColor: "#bbdefb",
      },
    },
  },
  listItemContent: {
    minWidth: 0, // Allow flex shrinking
    flex: 1,
  },
  primaryText: {
    fontWeight: 500,
    fontSize: "14px",
    color: "#333333",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "1.2",
    marginBottom: "2px",
  },
  secondaryText: {
    fontSize: "12px",
    color: "#666666",
    lineHeight: "1.2",
  },
  deleteButton: {
    padding: "4px",
    marginLeft: "8px",
    opacity: 0.7,
    "&:hover": {
      opacity: 1,
      backgroundColor: "#f5f5f5",
    },
  },
  emptyState: {
    textAlign: "center",
    color: "#999999",
    fontSize: "14px",
    padding: "20px",
    fontStyle: "italic",
  },
});
type RunPayload = any;
type RunEntry = {
  runPayload: RunPayload;
  workflowRecords: WorkflowRecord[];
  createTime: number;
};
type WorkflowRecord = {
  workflowId: string;
};

type RunHistoryProps = {
  onRunSelected: (run: RunEntry | undefined) => void;
};

const RUN_HISTORY_SCHEMA_VER = 1;

const RunHistory = forwardRef((props: RunHistoryProps, ref) => {
  const { onRunSelected } = props;
  const { stack } = useEnv();
  const classes = useStyles();
  const [selectedCreateTime, setSelectedCreateTime] = useState<
    number | undefined
  >(undefined);
  const [runHistory, setRunHistory]: readonly [
    RunEntry[],
    (v: RunEntry[]) => void
  ] = useLocalStorage(`runHistory_${stack}_${RUN_HISTORY_SCHEMA_VER}`, []);

  useImperativeHandle(ref, () => ({
    pushNewRun: (runPayload: RunPayload) => {
      const createTime = new Date().getTime();
      const newRun = {
        runPayload: runPayload,
        workflowRecords: [],
        createTime: createTime,
      };
      setRunHistory([newRun, ...runHistory]);
      setSelectedCreateTime(createTime);

      return newRun;
    },
    updateRun: (createTime: number, workflowId: string) => {
      const idx = runHistory.findIndex((v) => v.createTime === createTime);
      const currRun = runHistory[idx];
      const oldRecords = currRun.workflowRecords;
      const updatedRun = {
        runPayload: currRun.runPayload,
        workflowRecords: [
          {
            workflowId: workflowId,
          },
          ...oldRecords,
        ],
        createTime: currRun.createTime,
      };

      setRunHistory(immutableReplaceAt(runHistory, idx, updatedRun));
      onRunSelected(updatedRun);
    },
  }));

  function handleSelectRun(run: RunEntry) {
    if (onRunSelected) onRunSelected(run);
    setSelectedCreateTime(run.createTime);
  }

  function handleDeleteAll() {
    setClickedDeleteBtn({
      openDialog: true,
      header: "Delete",
      message: "Delete all run history in this browser?",
    });
  }

  function handleDeleteItem(run: RunEntry) {
    const newHistory = runHistory.filter(
      (v) => v.createTime !== run.createTime
    );
    if (newHistory.length > 0) {
      setSelectedCreateTime(newHistory[0].createTime);
      onRunSelected(newHistory[0]);
    } else {
      setSelectedCreateTime(undefined);
      onRunSelected(undefined);
    }
    setRunHistory(newHistory);
  }

  const [clickedDeleteBtn, setClickedDeleteBtn] = useState({
    openDialog: false,
    header: "",
    message: "",
  });

  const ConformationDialog = useMemo(() => {
    const { openDialog, header, message } = clickedDeleteBtn;
    if (openDialog) {
      return (
        <ConfirmChoiceDialog
          header={header}
          message={message}
          handleConfirmationValue={(confirm: any) => {
            if (confirm) {
              setRunHistory([]);
              setClickedDeleteBtn({
                openDialog: false,
                header: "",
                message: "",
              });
            } else {
              setClickedDeleteBtn({
                openDialog: false,
                header: "",
                message: "",
              });
            }
          }}
          open={openDialog}
        />
      );
    } else {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedDeleteBtn]);

  return (
    <>
      <div className={classes.sidebar}>
        <Toolbar className={classes.toolbar}>
          <Text level={0} className={classes.title}>
            Run History
          </Text>
          <Tooltip title="Delete all runs" placement="top">
            <IconButton
              onClick={handleDeleteAll}
              style={{ color: colors.failure }}
              size="small"
            >
              <Icons.DeleteOutlineOutlined />
            </IconButton>
          </Tooltip>
        </Toolbar>
        <List className={classes.list}>
          {runHistory.map((run) => (
            <ListItem
              key={run.createTime}
              selected={selectedCreateTime === run.createTime}
              onClick={() => handleSelectRun(run)}
              className={classes.listItem}
            >
              <div className={classes.listItemContent}>
                <Tooltip
                  title={run.runPayload.name || "Unnamed workflow"}
                  placement="top"
                  enterDelay={500}
                >
                  <div className={classes.primaryText}>
                    {run.runPayload.name || "Unnamed workflow"}
                  </div>
                </Tooltip>
                <div className={classes.secondaryText}>
                  {formatRelative(new Date(run.createTime), new Date())}
                </div>
              </div>
              <Tooltip title="Delete run" placement="top">
                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent item selection when clicking delete
                    handleDeleteItem(run);
                  }}
                  className={classes.deleteButton}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
          {_.isEmpty(runHistory) && (
            <div className={classes.emptyState}>No saved runs.</div>
          )}
        </List>
      </div>
      {ConformationDialog}
    </>
  );
});

export default RunHistory;
